import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../auth/auth-context';
import { useNavigate, useLocation } from 'react-router-dom';

type FormData = { username: string; password: string }

const isDev = process.env.NODE_ENV === 'development';

const LoginForm: React.FC = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState<FormData>({ username: '', password: '' });
  const [isLoading, setLoading] = useState<boolean>(false);
  const [loginErr, setLoginErr] = useState<string>('');
  const [signupMsg, setSignupMsg] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();

  // Tjek URL for query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newlySignedUp = params.get('newlySignedUp');
    const username = params.get('username');

    if (newlySignedUp === 'true' && username) {
      setSignupMsg(`Your account "${username}" has been successfully activated. You can now log in and start using your account.`);
      setFormData({ username, password: '' });
    }
  }, [location.search]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      setLoading(true);
      const origin = isDev ? 'http://localhost:3050/' : 'https://api.affdash.nordpar.com/';
      const res = await fetch(origin + 'login', { 
        body: JSON.stringify(formData), 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }
      });

      if(res.status >= 400) {
        const resp = await res.text();
        setLoginErr(resp);
        return;
      }

      const { token } = await res.json();
      login(token);
      setLoginErr('');
      navigate('/');
    } catch {
      setLoginErr('Unable to login');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container d-flex justify-content-center align-items-center min-vh-100">
      <div className="w-100 px-3" style={{ maxWidth: 480 }}>
        <Card className="shadow-sm rounded-3 border-0">
          <Card.Body className="p-5">

            {/* Title */}
            <div className="text-center mb-4">
              <h3 className="fw-bold mb-2">Welcome</h3>
              <p className="text-muted mb-0" style={{ opacity: 0.8 }}>Log in to your account</p>
            </div>

            {/* Signup success message */}
            {signupMsg && (
              <Alert variant="success" className="text-center fw-medium">
                {signupMsg}
              </Alert>
            )}

            {/* Form */}
            <Form onSubmit={handleSubmit} noValidate>
              <Form.Group className="mb-3" controlId="username">
                <Form.Label>Username or Email</Form.Label>
                <Form.Control
                  type="text"
                  name="username"
                  placeholder="Enter username or email"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                  autoComplete="username"
                />
              </Form.Group>

              <Form.Group className="mb-4" controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength={8}
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </Form.Group>

              <Button 
                variant="primary" 
                type="submit" 
                className="w-100 py-2"
                disabled={isLoading}
              >
                {isLoading ? 'Logging in…' : 'Login'}
              </Button>

              <div className="text-center mt-2">
                <Button
                  variant="link"
                  className="p-0 text-muted text-decoration-none fw-normal"
                  style={{ fontSize: '0.8rem' }}
                  type="button"
                  disabled={isLoading}
                  onClick={() => navigate('/forgot-password', {
                    state: { email: formData.username.includes('@') ? formData.username.trim() : '' }
                  })}
                >
                  Forgot password?
                </Button>
              </div>

              {isLoading && (
                <Spinner className="d-flex mt-3 mx-auto" animation="border" role="status" />
              )}

              {loginErr && (
                <div className="text-danger text-center fw-bold mt-3">
                  {loginErr}
                </div>
              )}
            </Form>

            {/* Footer */}
            <div className="text-center mt-4">
              <span className="text-muted me-1">Don’t have an account?</span>
              <Button
                variant="link"
                className="p-0 align-baseline"
                disabled={isLoading}
                onClick={() => navigate('/signup')}
              >
                Sign up and try free for 14 days
              </Button>
            </div>
            
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}

export default LoginForm;
